import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Approval } from '@bliks-io/contracts';
import { Badge, DataTable, createColumnHeader, type DataTableColumnDef } from '@bliks-io/ui';
import { useApprovals } from '@/api/queries/useApprovals';
import { formatDate } from '@/lib/formatters';
import { ApprovalDialog } from './ApprovalDialog';

const STATUSES: Approval['status'][] = [
  'pending',
  'approved',
  'rejected',
  'overridden',
  'executed',
  'withdrawn'
];

const SCHEME = {
  pending: 'warning',
  approved: 'success',
  executed: 'success',
  rejected: 'error',
  overridden: 'error',
  withdrawn: 'neutral'
} as const;

/**
 * "Freigaben" (plan 053): admins see every request of the workspace — the
 * overridden ones among them to look at afterwards — others the ones they
 * asked for or are named on. Gone while there are none.
 */
export function ApprovalsSection() {
  const { t, i18n } = useTranslation();
  const { data, isLoading } = useApprovals();
  const [open, setOpen] = useState<string | null>(null);

  const columns = useMemo<DataTableColumnDef<Approval>[]>(
    () => [
      {
        accessorKey: 'label',
        meta: { label: t('approvals.columns.label'), filterType: 'text' },
        header: createColumnHeader(t('approvals.columns.label')),
        enableColumnFilter: true
      },
      {
        accessorKey: 'status',
        meta: {
          label: t('approvals.columns.status'),
          filterType: 'select',
          filterOptions: STATUSES.map((s) => ({ value: s, label: t(`approvals.status.${s}`) }))
        },
        header: createColumnHeader(t('approvals.columns.status')),
        cell: ({ row }) => (
          <Badge colorScheme={SCHEME[row.original.status]} shape="pill">
            {t(`approvals.status.${row.original.status}`)}
          </Badge>
        ),
        enableColumnFilter: true
      },
      {
        id: 'requestedBy',
        accessorFn: (row) => row.requestedBy.name,
        meta: { label: t('approvals.columns.requestedBy'), filterType: 'text' },
        header: createColumnHeader(t('approvals.columns.requestedBy')),
        enableColumnFilter: true
      },
      {
        id: 'reviewers',
        accessorFn: (row) => row.reviewers.map((r) => r.name).join(', '),
        meta: { label: t('approvals.columns.reviewers'), filterType: 'text' },
        header: createColumnHeader(t('approvals.columns.reviewers')),
        enableColumnFilter: true
      },
      {
        accessorKey: 'requestedAt',
        meta: { label: t('approvals.columns.requestedAt') },
        header: createColumnHeader(t('approvals.columns.requestedAt')),
        cell: ({ row }) => (
          <span className="whitespace-nowrap">
            {formatDate(row.original.requestedAt, i18n.language)}
          </span>
        )
      }
    ],
    [t, i18n.language]
  );

  const approvals = data?.approvals ?? [];
  if (!isLoading && approvals.length === 0) return null;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold leading-6">{t('approvals.title')}</h2>
        <p className="mt-1 text-sm font-normal leading-5 text-gray-8">
          {data?.scope === 'all' ? t('approvals.descriptionAll') : t('approvals.descriptionMine')}
        </p>
      </div>
      <DataTable
        columns={columns}
        data={approvals}
        isLoading={isLoading}
        emptyMessage={t('approvals.empty')}
        enableGlobalFilter
        globalSearchPlaceholder={t('approvals.search')}
        enableColumnFilters
        tableId="approvals"
        initialSorting={[{ id: 'requestedAt', desc: true }]}
        onRowClick={(row) => setOpen(row.original._id)}
      />
      <ApprovalDialog approvalId={open} onClose={() => setOpen(null)} />
    </div>
  );
}
